import { midiToFrequency } from '@/lib/music/notes';

let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;

type WindowWithWebkit = Window & { webkitAudioContext?: typeof AudioContext };

export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioContext && masterGain) return audioContext;

  const w = window as WindowWithWebkit;
  const Ctor = window.AudioContext || w.webkitAudioContext;
  if (!Ctor) return null;

  audioContext = new Ctor();
  masterGain = audioContext.createGain();
  masterGain.gain.value = 0.85;
  masterGain.connect(audioContext.destination);
  return audioContext;
}

export function resumeAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume();
  }
}

type WaveShape = 'sine' | 'square' | 'sawtooth' | 'triangle';

interface TonePartials {
  ratio: number;
  gain: number;
  type: WaveShape;
}

const PIANO_PARTIALS: TonePartials[] = [
  { ratio: 1, gain: 1, type: 'triangle' },
  { ratio: 2, gain: 0.26, type: 'sine' },
  { ratio: 3, gain: 0.12, type: 'sine' },
  { ratio: 4.2, gain: 0.05, type: 'sine' },
];

/**
 * Agenda uma nota (por número MIDI) para tocar em um instante específico do
 * relógio de áudio. Usado pelo metrônomo e pelos exercícios de ritmo.
 */
export function playMidiAt(midi: number, when: number, duration = 0.5, velocity = 1): void {
  const ctx = getAudioContext();
  if (!ctx || !masterGain) return;
  if (ctx.state === 'suspended') void ctx.resume();

  const start = Math.max(when, ctx.currentTime + 0.005);
  const frequency = midiToFrequency(midi);

  const voice = ctx.createGain();
  voice.gain.setValueAtTime(0.0001, start);
  voice.gain.linearRampToValueAtTime(0.3 * velocity, start + 0.008);
  voice.gain.exponentialRampToValueAtTime(0.0006, start + Math.max(duration, 0.12));
  voice.connect(masterGain);

  PIANO_PARTIALS.forEach((partial) => {
    const osc = ctx.createOscillator();
    osc.type = partial.type;
    osc.frequency.value = frequency * partial.ratio;
    const gain = ctx.createGain();
    gain.gain.value = partial.gain;
    osc.connect(gain);
    gain.connect(voice);
    osc.start(start);
    osc.stop(start + duration + 0.08);
  });
}

/**
 * Toca uma nota (por número MIDI) com um timbre de piano simples.
 */
export function playMidi(midi: number, duration = 0.95, velocity = 1): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  playMidiAt(midi, ctx.currentTime, duration, velocity);
}

/** Toca várias notas em sequência, uma a cada `gap` segundos. */
export function playSequence(midis: number[], gap = 0.75, duration = 0.7): void {
  midis.forEach((midi, index) => {
    window.setTimeout(() => {
      playMidi(midi, duration);
    }, index * gap * 1000);
  });
}

/** Som curto de metrônomo / feedback. */
export function playClick(accent = false, delaySeconds = 0): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();

  const start = ctx.currentTime + delaySeconds;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.value = accent ? 1500 : 950;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(accent ? 0.32 : 0.2, start + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.06);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + 0.08);
}

/** Sinal sonoro suave de acerto/erro. */
export function playFeedback(correct: boolean): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();
  const notes = correct ? [880, 1174] : [320, 240];
  notes.forEach((frequency, index) => {
    const start = ctx.currentTime + index * 0.08;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(0.16, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.2);
  });
}

/**
 * Agenda um clique de metrônomo em um instante absoluto do relógio de áudio.
 * Retorna uma função para cancelar (usada ao pausar o exercício de ritmo).
 */
export function scheduleClickAt(time: number, accent: boolean): () => void {
  const ctx = getAudioContext();
  if (!ctx) return () => {};
  if (ctx.state === 'suspended') void ctx.resume();

  const start = Math.max(time, ctx.currentTime + 0.001);
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.value = accent ? 1500 : 950;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(accent ? 0.32 : 0.2, start + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.06);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + 0.08);

  return () => {
    try {
      osc.stop();
    } catch {
      /* já parado */
    }
  };
}